import commonResource from '../../locales/th/common.json';
import {
  getSubcategories,
  PostMainCategory,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import {
  getVideoTypes,
  VideoType,
} from '../../common/helpers/domain/videoTypes';
import { PropertyType } from '../../common/helpers/domain/propertyTypes';
import { isNotShowingPovContent } from '../../common/helpers/data/landingPages/fetchPropertyTypeLandingPageData';
import { SearchParams } from '../../pages/searchResultPage/SearchResultPage';

export const getNoTabDefinition = (
  propertyType?: PropertyType,
  postCategory?: PostSubcategory[],
  postTag?: string,
): ArticleFeedTabDefinition[] => {
  return [
    {
      key: 'none',
      displayText: '',
      requestParam: {
        propertyType: propertyType ? [propertyType] : null,
        postCategory: postCategory?.length ? postCategory : null,
        postTag,
      },
    },
  ];
};

export const getPropertyTypeTabDefinition = (
  postCategory?: PostSubcategory[],
): ArticleFeedTabDefinition[] => {
  const result: ArticleFeedTabDefinition[] = [];

  const isPreviewCategory =
    postCategory?.length === 1 && postCategory[0] === PostSubcategory.Preview;
  const isPointOfViewCategory =
    postCategory?.length === 1 &&
    postCategory[0] === PostSubcategory.PointOfView;

  result.push({
    key: 'all',
    displayText: commonResource.propertyTypes.all,
    requestParam: {
      postCategory: postCategory?.length ? postCategory : null,
    },
  });
  result.push({
    key: 'condominium',
    displayText: commonResource.propertyTypes.condominium,
    requestParam: {
      propertyType: [PropertyType.Condominium],
      ...(postCategory?.length ? { postCategory } : {}),
    },
  });
  result.push({
    key: 'house',
    displayText: commonResource.propertyTypes.house,
    requestParam: {
      propertyType: [PropertyType.House],
      ...(postCategory?.length ? { postCategory } : {}),
    },
  });
  if (!isPointOfViewCategory) {
    result.push({
      key: 'townhome',
      displayText: commonResource.propertyTypes.townhome,
      requestParam: {
        propertyType: [PropertyType.Townhome],
        ...(postCategory?.length ? { postCategory } : {}),
      },
    });
    result.push({
      key: 'holidayHomes',
      displayText: commonResource.propertyTypes.holidayHomes,
      requestParam: {
        propertyType: [PropertyType.HolidayHomes],
        ...(postCategory?.length ? { postCategory } : {}),
      },
    });
    result.push({
      key: 'homeOffice',
      displayText: commonResource.propertyTypes.homeOffice,
      requestParam: {
        propertyType: [PropertyType.HomeOffice],
        ...(postCategory?.length ? { postCategory } : {}),
      },
    });
    result.push({
      key: 'mixedUse',
      displayText: commonResource.propertyTypes.mixedUse,
      requestParam: {
        propertyType: [PropertyType.MixedUse],
        ...(postCategory?.length ? { postCategory } : {}),
      },
    });
    result.push({
      key: 'commercial',
      displayText: commonResource.propertyTypes.commercial,
      requestParam: {
        propertyType: [PropertyType.Commercial],
        ...(postCategory?.length ? { postCategory } : {}),
      },
    });
  }
  if (!isPreviewCategory && !isPointOfViewCategory) {
    result.push({
      key: 'video',
      displayText: commonResource.postCategories.video.main,
      requestParam: {
        ...(postCategory?.length ? { postCategory } : {}),
        videoType: getVideoTypes(),
      },
    });
  }

  return result;
};

export const getSrpTabDefinition = (
  searchParams: SearchParams,
  freeText?: string,
): ArticleFeedTabDefinition[] => {
  const result: ArticleFeedTabDefinition[] = [];

  result.push({
    key: 'all',
    displayText: commonResource.postCategories.all,
    requestParam: {
      searchParams,
      freeText,
    },
  });
  result.push({
    key: 'preview',
    displayText: commonResource.postCategories.projectResearch.preview,
    requestParam: {
      postCategory: [PostSubcategory.Preview],
      searchParams,
      freeText,
    },
  });
  result.push({
    key: 'review',
    displayText: commonResource.postCategories.projectResearch.review,
    requestParam: {
      postCategory: [PostSubcategory.Review],
      searchParams,
      freeText,
    },
  });
  result.push({
    key: 'pointOfView',
    displayText: commonResource.postCategories.projectResearch.pov,
    requestParam: {
      postCategory: [PostSubcategory.PointOfView],
      searchParams,
      freeText,
    },
  });
  result.push({
    key: 'locationInsight',
    displayText: commonResource.postCategories.locationInsight.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.LocationInsight),
      searchParams,
      freeText,
    },
  });
  result.push({
    key: 'news',
    displayText: commonResource.postCategories.news.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.News),
      searchParams,
      freeText,
    },
  });
  result.push({
    key: 'guides',
    displayText: commonResource.postCategories.guides.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.Guides),
      searchParams,
      freeText,
    },
  });
  result.push({
    key: 'decor',
    displayText: commonResource.postCategories.decor.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.Decor),
      searchParams,
      freeText,
    },
  });
  result.push({
    key: 'video',
    displayText: commonResource.postCategories.video.main,
    requestParam: {
      videoType: getVideoTypes(),
      searchParams,
      freeText,
    },
  });

  return result;
};

export const getPostCategoryTabDefinition = (
  propertyType?: PropertyType,
): ArticleFeedTabDefinition[] => {
  const result: ArticleFeedTabDefinition[] = [];

  result.push({
    key: 'all',
    displayText: commonResource.postCategories.all,
    requestParam: {
      propertyType: propertyType ? [propertyType] : null,
    },
  });
  result.push({
    key: 'preview',
    displayText: commonResource.postCategories.projectResearch.preview,
    requestParam: {
      postCategory: [PostSubcategory.Preview],
      propertyType: propertyType ? [propertyType] : null,
    },
  });
  result.push({
    key: 'review',
    displayText: commonResource.postCategories.projectResearch.review,
    requestParam: {
      postCategory: [PostSubcategory.Review],
      propertyType: propertyType ? [propertyType] : null,
    },
  });

  if (!isNotShowingPovContent(propertyType)) {
    result.push({
      key: 'pointOfView',
      displayText: commonResource.postCategories.projectResearch.pov,
      requestParam: {
        postCategory: [PostSubcategory.PointOfView],
        propertyType: propertyType ? [propertyType] : null,
      },
    });
  }
  result.push({
    key: 'locationInsight',
    displayText: commonResource.postCategories.locationInsight.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.LocationInsight),
    },
  });
  result.push({
    key: 'news',
    displayText: commonResource.postCategories.news.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.News),
    },
  });
  result.push({
    key: 'guides',
    displayText: commonResource.postCategories.guides.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.Guides),
    },
  });
  result.push({
    key: 'decor',
    displayText: commonResource.postCategories.decor.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.Decor),
    },
  });
  if (propertyType !== PropertyType.MixedUse) {
    result.push({
      key: 'video',
      displayText: commonResource.postCategories.video.main,
      requestParam: {
        propertyType: propertyType ? [propertyType] : null,
        videoType: getVideoTypes(),
      },
    });
  }

  return result;
};

export const getGuidesTabDefinition = (): ArticleFeedTabDefinition[] => {
  const result: ArticleFeedTabDefinition[] = [];

  result.push({
    key: 'all',
    displayText: commonResource.postCategories.all,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.Guides),
    },
  });
  result.push({
    key: 'buyAndSell',
    displayText: commonResource.postCategories.guides.buyAndSell,
    requestParam: {
      postCategory: [PostSubcategory.BuyAndSell],
    },
  });
  result.push({
    key: 'rent',
    displayText: commonResource.postCategories.guides.rent,
    requestParam: {
      postCategory: [PostSubcategory.Rent],
    },
  });

  result.push({
    key: 'finance',
    displayText: commonResource.postCategories.guides.finance,
    requestParam: {
      postCategory: [PostSubcategory.Finance],
    },
  });
  result.push({
    key: 'investment',
    displayText: commonResource.postCategories.guides.investment,
    requestParam: {
      postCategory: [PostSubcategory.Investment],
    },
  });

  return result;
};

export const getNewsTabDefinition = (): ArticleFeedTabDefinition[] => {
  const result: ArticleFeedTabDefinition[] = [];

  result.push({
    key: 'all',
    displayText: commonResource.postCategories.all,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.News),
    },
  });
  result.push({
    key: 'propertyNews',
    displayText: commonResource.postCategories.news.propertyNews,
    requestParam: {
      postCategory: [PostSubcategory.PropertyNews],
    },
  });
  result.push({
    key: 'generalNews',
    displayText: commonResource.postCategories.news.generalNews,
    requestParam: {
      postCategory: [PostSubcategory.GeneralNews],
    },
  });

  result.push({
    key: 'video',
    displayText: commonResource.postCategories.video.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.News),
      videoType: getVideoTypes(),
    },
  });

  return result;
};

export const getDecorTabDefinition = (): ArticleFeedTabDefinition[] => {
  const result: ArticleFeedTabDefinition[] = [];

  result.push({
    key: 'all',
    displayText: commonResource.postCategories.all,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.Decor),
    },
  });
  result.push({
    key: 'interior',
    displayText: commonResource.postCategories.decor.interiorInspiration,
    requestParam: {
      postCategory: [PostSubcategory.InteriorInspiration],
    },
  });
  result.push({
    key: 'renovation',
    displayText: commonResource.postCategories.decor.renovation,
    requestParam: {
      postCategory: [PostSubcategory.Renovation],
    },
  });

  result.push({
    key: 'maintenance',
    displayText: commonResource.postCategories.decor.maintenance,
    requestParam: {
      postCategory: [PostSubcategory.Maintenance],
    },
  });

  result.push({
    key: 'video',
    displayText: commonResource.postCategories.video.main,
    requestParam: {
      postCategory: getSubcategories(PostMainCategory.Decor),
      videoType: getVideoTypes(),
    },
  });

  return result;
};

export interface ArticleFeedTabDefinition {
  key: ArticleFeedTabKey;
  displayText: string;
  requestParam: {
    propertyType?: PropertyType[];
    postCategory?: PostSubcategory[];
    videoType?: VideoType[];
    postTag?: string;
    searchParams?: SearchParams;
    freeText?: string;
  };
}

export type ArticleFeedTabKey =
  | 'none'
  | 'all'
  | 'review'
  | 'preview'
  | 'pointOfView'
  | 'locationInsight'
  | 'news'
  | 'guides'
  | 'decor'
  | 'video'
  | 'condominium'
  | 'house'
  | 'townhome'
  | 'holidayHomes'
  | 'homeOffice'
  | 'mixedUse'
  | 'commercial'
  | 'interior'
  | 'renovation'
  | 'maintenance'
  | 'propertyNews'
  | 'generalNews'
  | 'buyAndSell'
  | 'rent'
  | 'finance'
  | 'investment';
